import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FolderIcon from '@mui/icons-material/Folder';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Unstable_Grid2';

import { DashboardFolderT, DashboardT } from 'business/definitions';
import ModalForm from 'components/Form/ModalForm';
import Loading from 'components/Loading';
import { trackEvent } from 'helpers/trackerHelpers';
import { useGetDashboardFolders } from 'routes/AllDashboards/hooks/dashboardFolders';
import { useCreateDashboardFolder } from 'routes/AllDashboards/hooks/createDashboardFolder';
import { useUpdateDashboard } from 'routes/AllDashboards/hooks/updateDashboard';
import FolderCard from './FolderCard';

type ActionType = 'main' | 'new';
type MainModalType = 'ok' | 'loading' | 'error';

type Props = {
    closeModal: () => void;
    dashboard: DashboardT;
    show: boolean;
    title: string;
};

const FolderForm = ({
    closeModal,
    dashboard,
    show,
    title,
}: Props): JSX.Element => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const [selectedAction, setSelectedAction] = useState<ActionType | null>(
        'main',
    );
    const [dataState, setDataState] = useState<MainModalType>('ok');

    const {
        data: dashboardFolders,
        isLoading: dashboardFoldersIsLoading,
        isError: dashboardFoldersIsError,
    } = useGetDashboardFolders();

    const sortedDashboardFolder = useMemo(
        () =>
            dashboardFolders
                ? dashboardFolders
                      .slice()
                      .sort((a, b) =>
                          a.name
                              .toLowerCase()
                              .localeCompare(b.name.toLowerCase()),
                      )
                : [],
        [dashboardFolders],
    );

    const showModals = {};
    const showMainDialog = {};

    ['main', 'new'].forEach((action) => {
        // @ts-expect-error [TS migration] Was not detected by flow
        showModals[action] = selectedAction === action;
    });
    ['loading', 'ok', 'error'].forEach((action) => {
        // @ts-expect-error [TS migration] Was not detected by flow
        showMainDialog[action] = dataState === action;
    });

    const createFolderMutation = useCreateDashboardFolder(dashboard);
    const createDashboardFolder = ({ name }: { name: string }) => {
        createFolderMutation.mutate({ name });
        closeModal();
    };

    const updateDashboardMutation = useUpdateDashboard(() => {
        trackEvent('Move dashboard to an existing folder', {
            dashboard: dashboard.slug,
        });
        queryClient.invalidateQueries({
            queryKey: ['dashboard_folders'],
        });
    });
    const updateDashboardFolder = ({
        dashboardFolder,
    }: {
        dashboardFolder: DashboardFolderT;
    }) => {
        updateDashboardMutation.mutate({
            newDashboardData: { folder: dashboardFolder },
            key: dashboard.key,
            slug: dashboard.slug,
        });
        closeModal();
    };

    if (dashboardFoldersIsError) {
        if (dataState !== 'error') {
            setDataState('error');
        }
    }
    if (dashboardFoldersIsLoading) {
        if (dataState !== 'loading') {
            setDataState('loading');
        }
    }
    if (!dashboardFoldersIsLoading && !dashboardFoldersIsLoading) {
        if (dataState !== 'ok') {
            setDataState('ok');
        }
    }

    const submit = () => {
        if (selectedIndex === null) {
            setSelectedAction('new');
        } else {
            updateDashboardFolder({
                dashboardFolder: sortedDashboardFolder.filter(
                    (folder) => folder.id === selectedIndex,
                )[0],
            });
        }
    };

    return (
        <div className="header-options-wrapper">
            {/* @ts-expect-error [TS migration] Was not detected by flow */}
            {showModals.new && (
                <ModalForm
                    show
                    closeModal={closeModal}
                    submit={createDashboardFolder}
                    submitText={t('common.add')}
                    title={t('dashboardFolders.create')}
                    fields={[
                        {
                            key: 'name',
                            name: 'title',
                            label: t('common.title'),
                            type: 'text',
                            validators: ['required'],
                        },
                    ]}
                    submitClass="e2e-modal-add-dashboard-button"
                />
            )}
            {/* @ts-expect-error [TS migration] Was not detected by flow */}
            {showModals.main && (
                <Dialog
                    open={show}
                    onClose={closeModal}
                    aria-labelledby="form-dialog-title"
                >
                    {/* @ts-expect-error [TS migration] Was not detected by flow */}
                    {showMainDialog.error && (
                        <div>
                            <Typography>{t('asyncComponent.error')}</Typography>
                            <DialogActions>
                                <Button onClick={closeModal}>
                                    {t('common.cancel')}
                                </Button>
                            </DialogActions>
                        </div>
                    )}
                    {/* @ts-expect-error [TS migration] Was not detected by flow */}
                    {showMainDialog.loading && (
                        <div>
                            <Box sx={{ m: '0 auto' }} data-testid="loading">
                                <Loading />
                            </Box>
                            <DialogActions>
                                <Button onClick={closeModal}>
                                    {t('common.cancel')}
                                </Button>
                            </DialogActions>
                        </div>
                    )}
                    {/* @ts-expect-error [TS migration] Was not detected by flow */}
                    {showMainDialog.ok && (
                        <div>
                            <DialogTitle>
                                <Typography variant="h5">{`${title}`}</Typography>
                            </DialogTitle>
                            <DialogContent>
                                <Grid container spacing={4} sx={{ p: '24px' }}>
                                    {sortedDashboardFolder
                                        .filter(
                                            (folder) =>
                                                !dashboard.folder ||
                                                folder.id !==
                                                    dashboard.folder.id,
                                        )
                                        .map((folder) => (
                                            <FolderCard
                                                key={folder.id}
                                                icon={<FolderIcon />}
                                                title={folder.name}
                                                isSelected={
                                                    selectedIndex === folder.id
                                                }
                                                onClick={() =>
                                                    setSelectedIndex(folder.id)
                                                }
                                            />
                                        ))}
                                    <FolderCard
                                        title={t('dashboardFolders.new')}
                                        isSelected={selectedIndex === null}
                                        onClick={() => setSelectedIndex(null)}
                                    />
                                </Grid>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={closeModal}>
                                    {t('common.cancel')}
                                </Button>
                                <Button
                                    onClick={submit}
                                    variant="contained"
                                    className="e2e-modal-update-dashboard-button"
                                >
                                    {t('common.confirm')}
                                </Button>
                            </DialogActions>
                        </div>
                    )}
                </Dialog>
            )}
        </div>
    );
};

export default FolderForm;
